/**
 * Paywall / pricing screen (P1-T17, redesigned 2026-10-17).
 *
 * - Full-screen (no top bar / tab bar): close X, Restore, title, optional
 *   limited-time promo banner, benefits, credit packs, membership plans and a
 *   sticky CTA.
 * - Everything numeric comes from GET /credits/pricing (CreditPricingConfig):
 *   prices, credits, bonus %, expiry, starter credits, promo. Nothing is
 *   hard-coded in the client.
 * - The CTA opens the order review (#/checkout/:id), which buys through Google
 *   Play in the Android app. Inside the app, prices are Play's local prices.
 *   Purchases are NEVER faked, and the promo banner only appears when the
 *   backend sends `promo`.
 */
import { api } from '../api/client.js';
import { navigate } from '../router.js';
import { authChanged, getUser, hasAccount } from '../state/auth.js';
import { openSignInSheet } from '../ui/signinSheet.js';
import { toast } from '../ui/toast.js';
import { escapeHtml } from '../components/placeholder.js';
import { restore } from '../billing/actions.js';
import { withStorePrices } from '../billing/play.js';
import { icon } from '../components/icon.js';
import type { CreditPack, CreditPricingConfig, SubscriptionTier } from '@loomia/shared';

const COMING_SOON_MSG = 'Checkout isn’t live yet — payments launching soon.';
const IMG = '/img/pricing';

const num = (n: number): string => n.toLocaleString('en-US');

function formatPrice(priceMinorUnits: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      priceMinorUnits / 100,
    );
  } catch {
    return `${(priceMinorUnits / 100).toFixed(2)} ${currency}`;
  }
}

const star = (cls = 'pw-star'): string =>
  `<img class="${cls}" src="${IMG}/credit-star.webp" alt="" width="180" height="180" decoding="async" />`;

function packCard(pack: CreditPack, popular: boolean): string {
  return `
    <button class="pw-pack${popular ? ' is-featured' : ''}" type="button" data-sel="${escapeHtml(pack.id)}" aria-pressed="false"
        aria-label="${num(pack.credits)} credits for ${escapeHtml(formatPrice(pack.priceMinorUnits, pack.currency))}">
      <span class="pw-frame" aria-hidden="true"></span>
      ${popular ? '<span class="pw-tab">Popular Pick</span>' : ''}
      ${pack.bonusPercent > 0 ? `<span class="pw-bonus">+${pack.bonusPercent}%</span>` : '<span class="pw-bonus is-none"></span>'}
      ${star()}
      <span class="pw-credits">${num(pack.credits)}</span>
      <span class="pw-credits-label">credits</span>
      <span class="pw-price">${escapeHtml(formatPrice(pack.priceMinorUnits, pack.currency))}</span>
    </button>`;
}

function planCard(sub: SubscriptionTier): string {
  const weekly = sub.interval === 'weekly';
  return `
    <button class="pw-plan${sub.bestValue ? ' is-featured' : ''}" type="button" data-sel="${escapeHtml(sub.id)}" aria-pressed="false"
        aria-label="${weekly ? 'Weekly' : 'Annual'} plan for ${escapeHtml(formatPrice(sub.priceMinorUnits, sub.currency))}">
      <span class="pw-frame" aria-hidden="true"></span>
      ${sub.bestValue ? '<span class="pw-tab">Top Value</span>' : ''}
      ${sub.bonusPercent > 0 ? `<span class="pw-disc"><b>+${sub.bonusPercent}%</b><i>more</i></span>` : ''}
      <span class="pw-plan-main">
        <span class="pw-plan-name">${weekly ? 'Weekly' : 'Annual'}</span>
        <span class="pw-plan-credits">${star('pw-star pw-star-sm')}${num(sub.creditsPerPeriod)} credits / ${weekly ? 'week' : 'year'}</span>
      </span>
      <span class="pw-plan-price">
        <b>${escapeHtml(formatPrice(sub.priceMinorUnits, sub.currency))}</b>
        <i>/ ${weekly ? 'week' : 'year'}</i>
      </span>
    </button>`;
}

function countdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const p = (n: number): string => String(n).padStart(2, '0');
  const hms = `${p(Math.floor((s % 86400) / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
  return d > 0 ? `${d}d ${hms}` : hms;
}

export function renderPaywall(root: HTMLElement, query?: URLSearchParams): void {
  /** Opened from the welcome screen: Close / sign-up continue to Home. */
  const fromWelcome = query?.get('from') === 'welcome';
  let alive = true;
  let offTimer: number | undefined;
  let selected: string | null = null;
  let pricing: CreditPricingConfig | null = null;
  let timer: number | undefined;

  root.innerHTML = `
    <div class="pw">
      <header class="pw-head">
        <button class="pw-close" type="button" id="pw-close" aria-label="Close">${icon('close')}</button>
        <button class="pw-restore" type="button" id="pw-restore">Restore</button>
      </header>
      <h1 class="pw-title">Loomia <span class="pw-pro">PRO</span></h1>
      <p class="pw-sub">Create more with AI photo &amp; video</p>
      <div id="pw-body"><p class="pw-loading">Loading…</p></div>
    </div>`;

  const body = (): HTMLElement | null => root.querySelector<HTMLElement>('#pw-body');

  const stop = (): void => {
    alive = false;
    if (timer !== undefined) window.clearInterval(timer);
    if (offTimer !== undefined) window.clearInterval(offTimer);
    root.removeEventListener('click', onClick);
    mo.disconnect();
  };

  const ctaLabel = (): string => {
    if (!pricing || !selected) return 'Choose an option';
    const pack = pricing.packs.find((p) => p.id === selected);
    if (pack) return `Get ${num(pack.credits)} credits · ${formatPrice(pack.priceMinorUnits, pack.currency)}`;
    const sub = pricing.subscriptions.find((s) => s.id === selected);
    if (sub) {
      return `Continue · ${formatPrice(sub.priceMinorUnits, sub.currency)} / ${sub.interval === 'weekly' ? 'week' : 'year'}`;
    }
    return 'Choose an option';
  };

  const paintSelection = (): void => {
    root.querySelectorAll<HTMLElement>('[data-sel]').forEach((el) => {
      const on = el.dataset.sel === selected;
      el.classList.toggle('is-selected', on);
      el.setAttribute('aria-pressed', String(on));
    });
    const label = root.querySelector<HTMLElement>('#pw-cta-label');
    if (label) label.textContent = ctaLabel();
  };

  function onClick(e: Event): void {
    const t = e.target as HTMLElement;
    const sel = t.closest<HTMLElement>('[data-sel]');
    if (sel?.dataset.sel) {
      selected = sel.dataset.sel;
      paintSelection();
      return;
    }
    if (t.closest('#pw-close') || t.closest('#pw-later')) {
      if (!fromWelcome && window.history.length > 1) window.history.back();
      else navigate('/home');
    } else if (t.closest('#pw-signup')) {
      const n = pricing?.starterCredits ?? 0;
      signUp(n > 0 ? `Claim your ${num(n)} free credits` : 'Create your free account', 'Enter your email and we’ll send you a sign-in link. No password needed.');
    } else if (t.closest('#pw-restore')) {
      void restore();
    } else if (t.closest('#pw-cta')) {
      // Review the order on #/checkout/:id. The real payment step is blocked on
      // P0-T04/P0-T05 (P1-T10) and the checkout screen never fakes a purchase.
      if (!hasAccount()) signUp('Sign in to buy credits', 'Enter your email and we’ll send you a sign-in link. No password needed.');
      else if (selected) navigate(`/checkout/${encodeURIComponent(selected)}`);
      else toast(COMING_SOON_MSG);
    } else if (t.closest('[data-legal]')) {
      toast('Available at launch.');
    } else if (t.closest('#pw-retry')) {
      void load();
    }
  }
  root.addEventListener('click', onClick);

  /** Sign-in sheet; once signed in, re-render (gift card disappears) or go Home from onboarding. */
  function signUp(title: string, note: string): void {
    openSignInSheet(title, note);
    // Several auth events can fire (e.g. a storage event before the tokens land): wait for a real account.
    const off = authChanged.on(() => {
      if (!alive) return off();
      if (!hasAccount()) return;
      off();
      if (fromWelcome) navigate('/home');
      else if (pricing) renderPricing(pricing);
    });
  }

  /** Sign-up gift (guests only) + first-day purchase offer. */
  const renderWelcomeOffers = (p: CreditPricingConfig): string => {
    const gift =
      !hasAccount() && p.starterCredits > 0
        ? `
        <section class="pw-gift-card" aria-label="Sign-up gift">
          <img class="pw-gift-img" src="${IMG}/gift.webp" alt="" width="420" height="420" decoding="async" />
          <div class="pw-gift-tx">
            <b>Sign up free · get ${num(p.starterCredits)} credits</b>
            <i>Enough to try your first creations today.</i>
          </div>
          <button class="btn btn-primary btn-block" type="button" id="pw-signup">Sign up &amp; claim ${num(p.starterCredits)} ✦</button>
          ${fromWelcome ? '<button class="pw-later" type="button" id="pw-later">Not now, just explore</button>' : ''}
        </section>`
        : '';
    const o = p.welcomeOffer;
    let offer = '';
    if (o && o.bonusPercent > 0) {
      const user = getUser();
      if (!user) {
        offer = `
          <section class="pw-first" aria-label="First-day offer">
            ${icon('bolt', { cls: 'pw-first-ic' })}
            <span><b>First-day deal: +${o.bonusPercent}% credits</b><i>On your first credit pack within ${o.windowHours}h of signing up.</i></span>
          </section>`;
      } else {
        const end = Date.parse(user.createdAt) + o.windowHours * 3_600_000;
        if (Number.isFinite(end) && end > Date.now()) {
          offer = `
            <section class="pw-first" aria-label="First-day offer">
              ${icon('bolt', { cls: 'pw-first-ic' })}
              <span><b>First-day deal: +${o.bonusPercent}% credits</b><i>On your first credit pack. Ends in <em id="pw-wcount" data-end="${end}">${countdown(end - Date.now())}</em></i></span>
            </section>`;
        }
      }
    }
    return gift + offer;
  };

  const startOfferCountdown = (): void => {
    if (offTimer !== undefined) window.clearInterval(offTimer);
    const el = root.querySelector<HTMLElement>('#pw-wcount');
    if (!el) return;
    const end = Number(el.dataset['end']);
    offTimer = window.setInterval(() => {
      const left = end - Date.now();
      if (!alive || left <= 0) {
        if (left <= 0) root.querySelector('.pw-first')?.remove();
        window.clearInterval(offTimer);
        return;
      }
      el.textContent = countdown(left);
    }, 1000);
  };

  // If the page unmounts (hash change re-renders), stop touching the DOM.
  const mo = new MutationObserver(() => {
    if (!root.isConnected) stop();
  });
  mo.observe(document.body, { childList: true, subtree: true });

  const renderPromo = (p: CreditPricingConfig): string => {
    const promo = p.promo;
    if (!promo) return '';
    const left = Date.parse(promo.endsAt) - Date.now();
    if (!Number.isFinite(left) || left <= 0) return '';
    return `
      <section class="pw-promo" aria-label="${escapeHtml(promo.label)}">
        <span class="pw-frame" aria-hidden="true"></span>
        <img class="pw-gift" src="${IMG}/gift.webp" alt="" width="420" height="420" decoding="async" />
        <span class="pw-promo-tx">
          <b>${escapeHtml(promo.label)}</b>
          <i>Ends in</i>
        </span>
        <span class="pw-count" id="pw-count" data-end="${escapeHtml(promo.endsAt)}">${countdown(left)}</span>
      </section>`;
  };

  const startCountdown = (): void => {
    const el = root.querySelector<HTMLElement>('#pw-count');
    if (!el) return;
    const end = Date.parse(el.dataset.end ?? '');
    timer = window.setInterval(() => {
      if (!alive) return;
      const left = end - Date.now();
      if (left <= 0) {
        root.querySelector('.pw-promo')?.remove();
        window.clearInterval(timer);
        return;
      }
      el.textContent = countdown(left);
    }, 1000);
  };

  const renderPricing = (p: CreditPricingConfig): void => {
    const host = body();
    if (!alive || !host) return;
    pricing = p;
    const packs = p.packs ?? [];
    const subs = p.subscriptions ?? [];

    // "Popular" comes from the backend; fall back to nothing (no fake social proof).
    const popularId = packs.find((x) => x.popular)?.id ?? null;

    const expiry =
      p.creditExpiryDays === null || p.creditExpiryDays === undefined
        ? { t: 'No expiry', d: 'Your credits never expire' }
        : { t: `${p.creditExpiryDays}-day validity`, d: `Credits are valid for ${p.creditExpiryDays} days` };

    const benefits = [
      { ic: 'auto_awesome', t: 'All AI tools', d: 'Photo, video & face swap' },
      { ic: 'toll', t: 'Pay on success', d: 'Failed runs are refunded' },
      ...(p.starterCredits > 0
        ? [{ ic: 'redeem', t: `${num(p.starterCredits)} free credits`, d: 'Included for new accounts' }]
        : []),
      { ic: p.creditExpiryDays == null ? 'all_inclusive' : 'schedule', t: expiry.t, d: expiry.d },
    ];

    if (!selected) {
      selected = subs.find((s) => s.bestValue)?.id ?? popularId ?? packs[0]?.id ?? subs[0]?.id ?? null;
    }

    host.innerHTML = `
      ${renderWelcomeOffers(p)}
      ${renderPromo(p)}

      <ul class="pw-benefits">
        ${benefits
          .map(
            (b) => `<li>${icon(b.ic, { cls: 'pw-b-ic' })}<span><b>${escapeHtml(b.t)}</b><i>${escapeHtml(b.d)}</i></span></li>`,
          )
          .join('')}
      </ul>

      <h2 class="pw-h2">Credit Packs</h2>
      ${
        packs.length === 0
          ? '<p class="pw-empty">Packs are being finalized — check back soon.</p>'
          : `<div class="pw-packs">${packs.map((x) => packCard(x, x.id === popularId)).join('')}</div>`
      }

      <h2 class="pw-h2">Membership Plans</h2>
      ${
        subs.length === 0
          ? '<p class="pw-empty">Plans are being finalized — check back soon.</p>'
          : `<div class="pw-plans">${subs.map(planCard).join('')}</div>`
      }

      <p class="pw-fine">
        <span class="pw-legal"><button type="button" data-legal="terms">Terms</button> · <button type="button" data-legal="privacy">Privacy</button></span>
      </p>

      <div class="pw-cta-wrap">
        <button class="pw-cta" type="button" id="pw-cta">
          <img src="${IMG}/cta-blank.webp" alt="" width="900" height="201" decoding="async" />
          <span class="pw-cta-tx"><span id="pw-cta-label"></span>${icon('arrow_forward')}</span>
        </button>
      </div>`;
    paintSelection();
    startCountdown();
    startOfferCountdown();
  };

  const load = async (): Promise<void> => {
    try {
      renderPricing(await withStorePrices(await api.getPricing()));
    } catch {
      const host = body();
      if (!alive || !host) return;
      host.innerHTML = `
        <div class="pw-error">
          <p>Couldn’t load pricing.</p>
          <button class="btn btn-secondary" id="pw-retry" type="button">Retry</button>
        </div>`;
    }
  };
  void load();
}
